import { useQueries } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { getSession } from '@/api/sessions';
import { getSessionMetrics } from '@/api/metrics';
import { VerdictBadge } from '@/components/common/StatusBadge';
import { Loading } from '@/components/common/States';
import { bucketPoints } from '@/lib/metrics';
import { fmtMbps, fmtMs, fmtPct, shortId } from '@/lib/format';
import type { Session, SessionMetricsResponse } from '@/types';

const COLORS = ['#54f0a8', '#5cc8ff', '#ffc24b', '#b98cff', '#ff5d6c', '#7de3c0'];

// Merge each session's RX-throughput series onto a shared "seconds into test"
// x-axis: [{ sec, s0: mbps, s1: mbps, ... }].
function overlay(metrics: (SessionMetricsResponse | undefined)[]): Record<string, number>[] {
  const bySec = new Map<number, Record<string, number>>();
  metrics.forEach((m, i) => {
    if (!m) return;
    const samples = bucketPoints(m.points);
    const t0 = samples[0]?.t ?? 0;
    for (const s of samples) {
      const sec = Math.round((s.t - t0) / 1000);
      const row = bySec.get(sec) ?? { sec };
      row[`s${i}`] = s.rx || s.tx || 0;
      bySec.set(sec, row);
    }
  });
  return [...bySec.values()].sort((a, b) => a.sec - b.sec);
}

export function CompareSessions() {
  const [sp] = useSearchParams();
  const ids = (sp.get('ids') || '').split(',').filter(Boolean);

  const sessionQ = useQueries({
    queries: ids.map((id) => ({ queryKey: ['session', id], queryFn: () => getSession(id) })),
  });
  const metricQ = useQueries({
    queries: ids.map((id) => ({ queryKey: ['cmp-metrics', id], queryFn: () => getSessionMetrics(id) })),
  });

  if (sessionQ.some((q) => q.isLoading)) return <Loading label="Loading sessions" />;
  const sessions = sessionQ.map((q) => q.data).filter(Boolean) as Session[];
  const data = overlay(metricQ.map((q) => q.data));

  const rows: { label: string; get: (s: Session) => string; best?: 'hi' | 'lo' }[] = [
    { label: 'Verdict', get: () => '' },
    { label: 'RX avg', get: (s) => fmtMbps(s.summary_json?.rx_avg_mbps ?? null), best: 'hi' },
    { label: 'RX peak', get: (s) => fmtMbps(s.summary_json?.rx_max_mbps ?? null), best: 'hi' },
    { label: 'Latency max', get: (s) => fmtMs(s.summary_json?.latency_max_ms), best: 'lo' },
    { label: 'Loss max', get: (s) => fmtPct(s.summary_json?.loss_max_pct), best: 'lo' },
    { label: 'Jitter max', get: (s) => fmtMs(s.summary_json?.jitter_max_ms), best: 'lo' },
    { label: 'Retransmits', get: (s) => String(s.summary_json?.retransmits_total ?? '—'), best: 'lo' },
    { label: 'Duration', get: (s) => `${s.duration_seconds}s` },
  ];

  return (
    <div className="fade-up">
      <div className="page-head spread">
        <div>
          <div className="eyebrow">compare</div>
          <div className="title">Session comparison</div>
          <div className="sub">RX throughput overlaid; summary + verdict side by side.</div>
        </div>
        <Link to="/sessions" className="btn btn-ghost">← Back</Link>
      </div>

      <div className="panel" style={{ padding: '16px 16px 8px', marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>RX throughput · Mbps (seconds into test)</div>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
            <CartesianGrid stroke="rgba(150,180,220,0.08)" vertical={false} />
            <XAxis dataKey="sec" type="number" tick={{ fill: '#3a4660', fontSize: 11 }} unit="s" />
            <YAxis tick={{ fill: '#3a4660', fontSize: 11 }} width={46} />
            <Tooltip contentStyle={{ background: 'rgba(8,11,17,0.95)', border: '1px solid var(--line-2)', fontSize: 12 }} />
            {sessions.map((s, i) => (
              <Line
                key={s.id}
                type="monotone"
                dataKey={`s${i}`}
                name={s.firmware || s.label || shortId(s.id)}
                stroke={COLORS[i % COLORS.length]}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="panel" style={{ overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th></th>
              {sessions.map((s, i) => (
                <th key={s.id}>
                  <span style={{ color: COLORS[i % COLORS.length] }}>●</span>{' '}
                  <Link to={`/sessions/${s.id}`} className="mono">{s.label || shortId(s.id)}</Link>
                  <div className="mono dim" style={{ fontSize: 11 }}>
                    {s.firmware || '—'}{s.tag ? ` · #${s.tag}` : ''}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td className="dim">{r.label}</td>
                {sessions.map((s) => (
                  <td key={s.id} className="mono">
                    {r.label === 'Verdict' ? <VerdictBadge verdict={s.verdict} /> : r.get(s)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
